import React, { useEffect, useRef } from 'react';

export default function InventoryHelpModal({ isOpen, onClose }) {
  const scrollRef = useRef(null);

  useEffect(() => {
    if (isOpen && scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Help"
    >
      <div
        className="w-full max-w-[960px] rounded-xl bg-white shadow-[0_12px_30px_rgba(0,0,0,0.12)] border border-gray-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-start justify-between border-b border-gray-200 bg-white px-5 py-4">
          <div>
            <h2 className="text-[20px] font-semibold text-[#111827]">Help</h2>
            <p className="text-[14px] text-[#6B7280]">A quick guide to the tools on this page</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-sm font-medium text-[#111827] hover:text-[#374151]"
            aria-label="Close"
          >
            Close
          </button>
        </div>

        <div
          ref={scrollRef}
          className="max-h-[60vh] overflow-y-auto px-6 py-5 text-[16px] leading-[1.6] text-[#111827]"
        >
          <div className="space-y-5">
            <section className="space-y-2">
              <h3 className="text-[17px] font-semibold text-[#111827]">You’re always in control</h3>
              <p className="text-[#4B5563]">Click into the title, price, or quantity box to make a change.</p>
              <p className="text-[#4B5563]">
                Any title, price, or quantity you change will display in{' '}
                <span style={{ color: 'rgb(59, 130, 246)' }} className="font-semibold">blue</span>.
              </p>
              <p className="text-[#4B5563]">
                Pricing decisions are made by you. Scucci records those decisions and prepares printable tags.
              </p>
            </section>

            <section className="space-y-2 border-t border-gray-100 pt-5">
              <h3 className="text-[17px] font-semibold text-[#111827]">How Selection Works</h3>
              <p className="text-[#4B5563]">Select items using the checkbox on the left of each row.</p>
              <p className="text-[#4B5563]">
                Once selected, print, put on hold, move items to another section, delete items entirely or export to CSV.
              </p>
            </section>

            <section className="space-y-4 border-t border-gray-100 pt-5">
              <h3 className="text-[17px] font-semibold text-[#111827]">Removing Items (Temporary or Permanent)</h3>
              <div className="space-y-2">
                <button
                  type="button"
                  disabled
                  className="inline-flex items-center h-8 px-3 rounded-md text-sm font-medium bg-rose-600 text-white border border-rose-600 opacity-70 cursor-not-allowed"
                >
                  Remove Selected Items
                </button>
                <p className="text-[#4B5563]">
                  After selecting items, this button gives you two options:
                </p>
              </div>
              <div className="space-y-3 pl-4">
                <div className="space-y-1">
                  <p className="text-[#4B5563]">
                    <span className="font-semibold text-[#111827]">On Hold</span> - Removes items from the
                    main list without deleting them. Use this if you want to price later or
                    temporarily set items aside.
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-[#4B5563]">
                    <span className="font-semibold text-[#111827]">Delete</span> - Permanently removes
                    items from the sale. Use this only when you’re sure the item should not be
                    included.
                  </p>
                </div>
              </div>
            </section>

            <section className="space-y-4 border-t border-gray-100 pt-5">
              <div className="space-y-2">
                <button
                  type="button"
                  disabled
                  className="inline-flex items-center h-8 px-3 rounded-md text-sm font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 opacity-70 cursor-not-allowed"
                >
                  Show Items On Hold
                </button>
                <p className="text-[#4B5563]">
                  Shows items you previously placed On Hold, and lets you restore them.
                </p>
              </div>
            </section>

            <section className="space-y-4 border-t border-gray-100 pt-5">
              <div className="space-y-2">
                <button
                  type="button"
                  disabled
                  className="h-8 px-3 rounded-md text-sm font-medium bg-indigo-600 text-white border border-indigo-600 opacity-70 cursor-not-allowed"
                >
                  Move Selected Items
                </button>
                <p className="text-[#4B5563]">
                  Moves selected items to a different section. Use this when items belong in another
                  room or were grouped incorrectly.
                </p>
              </div>
            </section>

            <section className="space-y-4 border-t border-gray-100 pt-5">
              <div className="space-y-2">
                <input
                  type="text"
                  disabled
                  placeholder="Search by title..."
                  className="inline-flex items-center h-8 px-3 rounded-md text-sm font-medium bg-indigo-50 text-indigo-700 border border-indigo-200 opacity-70 cursor-not-allowed"
                />
                <p className="text-[#4B5563]">Filters items as you type.</p>
              </div>
              <div className="space-y-2">
                <button
                  type="button"
                  disabled
                  className="inline-flex items-center h-8 px-3 rounded-md text-sm font-medium bg-slate-50 text-slate-700 border border-slate-200 opacity-70 cursor-not-allowed"
                >
                  Manual Tags
                </button>
                <p className="text-[#4B5563]">
                  Create quick price tags for bulk or unphotographed items. Example: “Cups — Qty 14 — $4”.
                </p>
                <p className="text-[#4B5563]">
                  Items created here appear in a separate Manual Tags section and are intended for
                  fast tag printing.
                </p>
              </div>
              <div className="space-y-2">
                <button
                  type="button"
                  disabled
                  className="inline-flex items-center h-8 px-3 rounded-md text-sm font-medium bg-orange-50 text-orange-700 border border-orange-200 opacity-70 cursor-not-allowed"
                >
                  Undo Last Edit
                </button>
                <p className="text-[#4B5563]">Reverts the most recent change made to inventory data.</p>
              </div>
            </section>

            <section className="space-y-4 border-t border-gray-100 pt-5">
              <div className="space-y-2">
                <button
                  type="button"
                  disabled
                  className="inline-flex items-center h-8 px-3 rounded-md text-sm font-medium bg-indigo-50 text-indigo-700 border border-indigo-200 opacity-70 cursor-not-allowed"
                >
                  Focus Mode
                </button>
                <p className="text-[#4B5563]">
                  Simplifies the table for fast pricing by showing only key fields.
                </p>
              </div>
              <div className="space-y-2">
                <button
                  type="button"
                  disabled
                  className="inline-flex items-center h-8 px-3 rounded-md text-sm font-medium bg-indigo-50 text-indigo-700 border border-indigo-200 opacity-70 cursor-not-allowed"
                >
                  Reset Grid
                </button>
                <p className="text-[#4B5563]">Restores any columns hidden during this session.</p>
                <p className="text-[#4B5563]">
                  Tip: On a phone, it’s possible to hide a column by accident by grabbing a column header and swiping up.
                </p>
                <p className="text-[#4B5563]">
                  If something disappears, use Reset Grid to bring the columns back.
                </p>
              </div>
            </section>

            <section className="space-y-4 border-t border-gray-100 pt-5">
              <div className="space-y-2">
                <button
                  type="button"
                  disabled
                  className="inline-flex items-center h-8 px-3 rounded-md text-sm font-medium bg-indigo-50 text-indigo-700 border border-indigo-200 opacity-70 cursor-not-allowed"
                >
                  Export CSV
                </button>
                <p className="text-[#4B5563]">
                  Exports the items you’re currently viewing (and/or selected items, depending on your
                  export option).
                </p>
              </div>
            </section>
          </div>
        </div>

        <div className="border-t border-gray-200 px-5 py-4 text-[13px] text-[#6B7280] space-y-1">
          <p>You set the prices. Scucci prepares the tags.</p>
          <p>Patent-pending · Powered by ScucciDEBORAH</p>
        </div>
      </div>
    </div>
  );
}

